import { Stack } from '@/components/layout';
import { FormInput } from '@/components/ui/form/form-input';
import { FormSelectInput } from '@/components/ui/form/form-select-input';
import { STRINGS } from '@/constants/strings';
import { STATE_OPTIONS } from '@/features/player/components/mutate-player/constants';
import { MutatePlayerForm } from '@/features/player/components/mutate-player/types';
import { useScaling } from '@/hooks';
import { Controller, useFormContext } from 'react-hook-form';

export const MutatePlayerContactSectionMobile = () => {
  const { scale, isTablet } = useScaling();
  const rowGap = scale(isTablet ? 12 : 18);
  const { control, setValue } = useFormContext<MutatePlayerForm>();
  const stateOptions = STATE_OPTIONS;

  return (
    <>
      <Controller
        name="parentFirstName"
        control={control}
        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
          <FormInput
            label={STRINGS.player.mutatePlayer.contact.parentFirstName}
            value={value}
            required
            trim
            status={error?.message ? 'error' : 'default'}
            errorMessage={error?.message}
            maxLength={100}
            onBlur={onBlur}
            onChangeText={parentFirstName => onChange(parentFirstName)}
            placeholder={STRINGS.player.mutatePlayer.contact.parentFirstName}
          />
        )}
      />
      <Controller
        name="parentLastName"
        control={control}
        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
          <FormInput
            label={STRINGS.player.mutatePlayer.contact.parentLastName}
            value={value}
            required
            trim
            status={error?.message ? 'error' : 'default'}
            errorMessage={error?.message}
            maxLength={100}
            onBlur={onBlur}
            onChangeText={parentLastName => onChange(parentLastName)}
            placeholder={STRINGS.player.mutatePlayer.contact.parentLastName}
          />
        )}
      />
      <Controller
        name="phoneNumber"
        control={control}
        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
          <FormInput
            label={STRINGS.player.mutatePlayer.contact.phoneNumber}
            value={value}
            required
            maxLength={24}
            trim
            status={error?.message ? 'error' : 'default'}
            errorMessage={error?.message}
            keyboardType="phone-pad"
            onBlur={onBlur}
            onChangeText={phoneNumber => onChange(phoneNumber)}
            placeholder={''}
            onCountryCodeChange={countryCode => {
              setValue('countryCode', countryCode);
            }}
            phoneNumber
          />
        )}
      />
      <Controller
        name="altPhoneNumber"
        control={control}
        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
          <FormInput
            label={STRINGS.player.mutatePlayer.contact.altPhoneNumber}
            required={false}
            value={value}
            maxLength={24}
            trim
            status={error?.message ? 'error' : 'default'}
            errorMessage={error?.message}
            keyboardType="phone-pad"
            onBlur={onBlur}
            onChangeText={altPhoneNumber => onChange(altPhoneNumber)}
            placeholder={''}
            phoneNumber
            onCountryCodeChange={countryCode => {
              setValue('altCountryCode', countryCode);
            }}
          />
        )}
      />
      <Controller
        name="email"
        control={control}
        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
          <FormInput
            label={STRINGS.player.mutatePlayer.contact.email}
            value={value}
            required
            status={error?.message ? 'error' : 'default'}
            errorMessage={error?.message}
            maxLength={100}
            onBlur={onBlur}
            trim
            onChangeText={email => onChange(email)}
            autoComplete="email"
            textContentType="emailAddress"
            autoCorrect={false}
            placeholder={STRINGS.player.mutatePlayer.contact.email}
          />
        )}
      />
      <Controller
        name="streetAddress"
        control={control}
        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
          <FormInput
            label={STRINGS.player.mutatePlayer.contact.streetAddress}
            required={false}
            value={value}
            status={error?.message ? 'error' : 'default'}
            errorMessage={error?.message}
            maxLength={200}
            onBlur={onBlur}
            onChangeText={streetAddress => onChange(streetAddress)}
            placeholder={STRINGS.player.mutatePlayer.contact.streetAddressPlaceholder}
          />
        )}
      />
      <Controller
        name="streetAddress2"
        control={control}
        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
          <FormInput
            label={STRINGS.player.mutatePlayer.contact.streetAddress2}
            required={false}
            value={value}
            status={error?.message ? 'error' : 'default'}
            errorMessage={error?.message}
            maxLength={200}
            onBlur={onBlur}
            onChangeText={streetAddress2 => onChange(streetAddress2)}
            placeholder={STRINGS.player.mutatePlayer.contact.streetAddress2Placeholder}
          />
        )}
      />
      <Controller
        name="city"
        control={control}
        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
          <FormInput
            label={STRINGS.player.mutatePlayer.contact.city}
            required={false}
            value={value}
            status={error?.message ? 'error' : 'default'}
            errorMessage={error?.message}
            maxLength={100}
            onBlur={onBlur}
            onChangeText={city => onChange(city)}
            placeholder={STRINGS.player.mutatePlayer.contact.city}
          />
        )}
      />
      <Stack direction="row" gap={rowGap} width="100%">
        <Stack flex={1}>
          <Controller
            name="state"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormSelectInput
                label={STRINGS.player.mutatePlayer.contact.state}
                required={false}
                options={stateOptions}
                value={value}
                status={error?.message ? 'error' : 'default'}
                errorMessage={error?.message}
                onValueChange={state => onChange(state)}
                placeholder={STRINGS.player.mutatePlayer.contact.state}
              />
            )}
          />
        </Stack>
        <Stack flex={1}>
          <Controller
            name="zipCode"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <FormInput
                label={STRINGS.player.mutatePlayer.contact.zipCode}
                required={false}
                value={value}
                status={error?.message ? 'error' : 'default'}
                errorMessage={error?.message}
                keyboardType="numeric"
                maxLength={15}
                onBlur={onBlur}
                onChangeText={zipCode => onChange(zipCode.replace(/[^0-9]/g, ''))}
                placeholder={STRINGS.player.mutatePlayer.contact.zipCode}
              />
            )}
          />
        </Stack>
      </Stack>
    </>
  );
};
